/* eslint-disable no-param-reassign */
import React, {
  forwardRef,
  useEffect,
  useState,
  useImperativeHandle,
} from 'react';

import { func, string, shape, bool, number } from 'prop-types';
import clsx from 'clsx';
import ClearIcon from 'assets/images/icons/clear.svg';
import WarningIcon from 'assets/images/icons/warning.svg';
import styles from './Input.module.scss';

const Input = forwardRef(
  (
    {
      className,
      type,
      inputRef,
      inputType,
      onChange,
      onFocus,
      onBlur,
      hasErrors,
      errorMessage,
      maxLength,
      placeholder,
      errorPattern,
      inputMode,
      required,
      name,
      tabiIndex,
    },
    ref
  ) => {
    const [cursor, setCursor] = useState(null);
    const [error, setError] = useState(hasErrors);

    const [inputValue, setInputValue] = useState('');
    const [hasFocues, setFocus] = useState(false);

    useEffect(() => {
      if (type === 'number' || type === 'tel') return;

      const input = inputRef?.current;

      if (cursor !== null) {
        input?.setSelectionRange(cursor, cursor);
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputRef, cursor]);

    const onInputChange = (e) => {
      setCursor(e.target.selectionStart);
      setInputValue(e.target.value);
      // eslint-disable-next-line no-unused-expressions
      onChange && onChange(e);
    };

    // eslint-disable-next-line no-unused-vars
    const validate = () => {
      const regExp = new RegExp(errorPattern);
      let notValid = false;

      if (!regExp.test(inputValue)) {
        notValid = true;
      } else {
        notValid = false;
      }

      if (required && inputValue === '') {
        notValid = true;
      }

      setError(notValid);
      return notValid;
    };

    const getValue = () => ({ value: inputValue, name });

    useImperativeHandle(ref, () => ({
      validate,
      getValue,
    }));

    useEffect(() => {
      if (hasErrors) {
        inputRef?.current.focus();
      }
    }, [hasErrors, inputRef]);

    const clear = (event) => {
      setInputValue('');
      event.preventDefault();
    };

    const focus = () => {
      setFocus(true);
      onFocus();
    };

    const blur = () => {
      setFocus(false);
      onBlur();
    };

    return (
      <div className={styles.wrapper}>
        {inputType === 'input' && (
          <input
            id="input"
            className={clsx(styles.input, className, {
              [styles.error]: error,
            })}
            ref={inputRef}
            type={type}
            onChange={onInputChange}
            onFocus={focus}
            value={inputValue}
            onBlur={blur}
            tabIndex={tabiIndex}
            maxLength={maxLength}
            placeholder={placeholder}
            inputMode={inputMode}
          />
        )}
        {inputType === 'textarea' && (
          <textarea
            id="input"
            className={clsx(styles.textarea, className, {
              [styles.error]: error,
            })}
            ref={inputRef}
            type={type}
            value={inputValue}
            onChange={onInputChange}
            tabIndex={tabiIndex}
            onFocus={focus}
            onBlur={blur}
            maxLength={maxLength}
            placeholder={placeholder}
            inputMode={inputMode}
          />
        )}
        {inputValue.length > 0 && (
          <button
            className={clsx(styles.clearBtn, {
              [styles.onFocus]: hasFocues,
            })}
            type="button"
            label="clear"
            onClick={(event) => {
              clear(event);
            }}
          >
            <ClearIcon />
          </button>
        )}
        {error && (
          <div className={styles.errorMessage}>
            <WarningIcon />
            <span>{errorMessage}</span>
          </div>
        )}
      </div>
    );
  }
);

Input.propTypes = {
  className: string,
  errorPattern: string,
  type: string,
  inputRef: shape(),
  onChange: func,
  onFocus: func,
  onBlur: func,
  hasErrors: bool,
  errorMessage: string,
  maxLength: number,
  placeholder: string,
  inputType: string,
  inputMode: string,
  required: bool,
  name: string,
  tabiIndex: number,
};

Input.defaultProps = {
  className: null,
  errorPattern: '',
  type: 'text',
  inputRef: null,
  onChange: () => {},
  onFocus: () => {},
  onBlur: () => {},
  hasErrors: false,
  maxLength: 1000,
  errorMessage: '',
  placeholder: '',
  inputType: 'input', // input || textareas,
  inputMode: null,
  required: false,
  name: '',
  tabiIndex: 0,
};

export default Input;
